import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useTimeContext } from "../pages/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  TrendingUp, AlertTriangle, Clock, Users, Building2, ChevronDown,
  ArrowUpRight, ArrowDownRight, Calendar, Wallet, Target, BarChart3
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => { if (n == null) return '--'; const a = Math.abs(n); const s = n < 0 ? '-' : ''; if (a >= 1e7) return `${s}${(a/1e7).toFixed(2)} Cr`; if (a >= 1e5) return `${s}${(a/1e5).toFixed(2)} L`; if (a >= 1e3) return `${s}${(a/1e3).toFixed(1)}K`; return s + a.toLocaleString(); };
const AGEING_COLORS = ['#10B981', '#3B82F6', '#F59E0B', '#F97316', '#EF4444', '#991B1B'];
const GRADE_COLORS = { A: 'bg-emerald-100 text-emerald-700', B: 'bg-blue-100 text-blue-700', C: 'bg-amber-100 text-amber-700', D: 'bg-red-100 text-red-700' };

export default function CollectionOutlook() {
  const { token } = useTimeContext();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedProjects, setExpandedProjects] = useState({});
  const [pipelineTab, setPipelineTab] = useState("next_30");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/kpi/collection-outlook`, { headers: { Authorization: `Bearer ${token}` } });
      setData(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- API/axios are module constants, setters are stable
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <div className="text-center py-12"><div className="inline-block h-6 w-6 border-2 border-slate-200 border-t-blue-600 rounded-full animate-spin" /></div>;
  if (!data) return <p className="text-sm text-slate-400 text-center py-8">No outlook data available</p>;

  const d = data;
  const forecast = d.forecast || [];
  const projects = d.project_outstanding || [];
  const ageing = d.ageing || [];
  const efficiency = d.efficiency || [];
  const bridge = d.bridge || {};
  const behavior = d.behavior || [];
  const pipeline = d.pipeline || {};

  const toggleProject = (p) => setExpandedProjects(prev => ({ ...prev, [p]: !prev[p] }));

  return (
    <div className="space-y-5" data-testid="collection-outlook">

      {/* ── 1. Expected Collection Forecast (Current Month) ── */}
      <Card className="bg-gradient-to-br from-[#0F1D32] to-[#1a2d4a] rounded-2xl border-0 text-white shadow-lg" data-testid="forecast-hero">
        <CardContent className="p-5 sm:p-6">
          <p className="text-[10px] font-bold text-blue-300 uppercase tracking-[0.15em] mb-4">Expected Collection — {forecast[0]?.month}</p>
          {forecast[0] && (
            <div>
              <p className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: 'Manrope' }}>{fmt(forecast[0].total)}</p>
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 bg-red-500/10 rounded-xl"><p className="text-[9px] text-red-300 font-semibold uppercase">Overdue</p><p className="text-lg font-bold text-red-200 mt-1" style={{ fontFamily: 'Manrope' }}>{fmt(forecast[0].overdue)}</p></div>
                <div className="p-3 bg-blue-500/10 rounded-xl"><p className="text-[9px] text-blue-300 font-semibold uppercase">Current</p><p className="text-lg font-bold text-blue-200 mt-1" style={{ fontFamily: 'Manrope' }}>{fmt(forecast[0].current_demands)}</p></div>
                <div className="p-3 bg-emerald-500/10 rounded-xl"><p className="text-[9px] text-emerald-300 font-semibold uppercase">Pipeline</p><p className="text-lg font-bold text-emerald-200 mt-1" style={{ fontFamily: 'Manrope' }}>{fmt(forecast[0].upcoming_pipeline)}</p></div>
              </div>
              {forecast[0].top_projects?.length > 0 && (
                <div className="mt-4 pt-3 border-t border-white/10">
                  <p className="text-[9px] text-slate-400 mb-2 uppercase font-semibold tracking-wider">Top Projects by Outstanding</p>
                  {forecast[0].top_projects.slice(0, 5).map(([name, val], j) => (
                    <p key={j} className="text-[11px] text-slate-300 flex justify-between py-0.5"><span className="truncate mr-3">{name}</span><span className="font-bold text-white">{fmt(val)}</span></p>
                  ))}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Summary KPIs ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 sm:gap-3">
        <div className="kpi-card"><div className="flex items-center gap-1.5 mb-1.5"><Wallet className="h-4 w-4 text-red-500" /><span className="text-[9px] sm:text-xs font-semibold text-slate-400 uppercase">Total Outstanding</span></div><p className="text-lg font-bold text-red-600" style={{ fontFamily: 'Manrope' }}>{fmt(d.total_outstanding)}</p></div>
        <div className="kpi-card"><div className="flex items-center gap-1.5 mb-1.5"><Target className="h-4 w-4 text-blue-500" /><span className="text-[9px] sm:text-xs font-semibold text-slate-400 uppercase">Total Demanded</span></div><p className="text-lg font-bold text-blue-600" style={{ fontFamily: 'Manrope' }}>{fmt(d.total_demanded)}</p></div>
        <div className="kpi-card"><div className="flex items-center gap-1.5 mb-1.5"><AlertTriangle className="h-4 w-4 text-amber-500" /><span className="text-[9px] sm:text-xs font-semibold text-slate-400 uppercase">Interest Accrued</span></div><p className="text-lg font-bold text-amber-600" style={{ fontFamily: 'Manrope' }}>{fmt(d.total_interest)}</p></div>
        <div className="kpi-card"><div className="flex items-center gap-1.5 mb-1.5"><Users className="h-4 w-4 text-indigo-500" /><span className="text-[9px] sm:text-xs font-semibold text-slate-400 uppercase">Projects</span></div><p className="text-lg font-bold text-indigo-600" style={{ fontFamily: 'Manrope' }}>{projects.length}</p></div>
      </div>

      {/* ── 3. Ageing Analysis ── */}
      <Card className="bg-white rounded-2xl border-slate-200/60 shadow-sm" data-testid="ageing-chart">
        <CardHeader className="pb-2 border-b border-slate-100"><CardTitle className="text-sm font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>Receivables Ageing Analysis</CardTitle></CardHeader>
        <CardContent className="pt-4">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={ageing} barCategoryGap="20%">
              <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
              <XAxis dataKey="range" tick={{ fontSize: 10, fill: '#475569' }} />
              <YAxis tick={{ fontSize: 10, fill: '#94A3B8' }} tickFormatter={fmt} />
              <Tooltip contentStyle={{ borderRadius: '10px', border: '1px solid #E2E8F0', fontSize: '11px' }} formatter={(v) => fmt(v)} />
              <Bar dataKey="amount" name="Outstanding" radius={[6, 6, 0, 0]}>{ageing.map((_, i) => <Cell key={i} fill={AGEING_COLORS[i]} />)}</Bar>
            </BarChart>
          </ResponsiveContainer>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-1.5 mt-3">
            {ageing.map((a, i) => (
              <div key={i} className="text-center p-2 rounded-lg" style={{ background: `${AGEING_COLORS[i]}10` }}>
                <p className="text-xs font-bold" style={{ color: AGEING_COLORS[i] }}>{a.count}</p>
                <p className="text-[8px] text-slate-500">{a.range} days</p>
                <p className="text-[9px] font-semibold text-slate-700">{fmt(a.amount)}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* ── 5. Collection Bridge ── */}
      <Card className="bg-white rounded-2xl border-slate-200/60 shadow-sm" data-testid="bridge">
        <CardHeader className="pb-2 border-b border-slate-100"><CardTitle className="text-sm font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>Monthly Collection Bridge — {bridge.month}</CardTitle></CardHeader>
        <CardContent className="pt-4">
          <div className="grid grid-cols-4 gap-3 text-center">
            <div className="p-3 bg-slate-50 rounded-xl"><p className="text-[9px] font-bold text-slate-400 uppercase">Opening</p><p className="text-base font-bold text-slate-800 mt-1" style={{ fontFamily: 'Manrope' }}>{fmt(bridge.opening_outstanding)}</p></div>
            <div className="p-3 bg-red-50 rounded-xl"><p className="text-[9px] font-bold text-red-400 uppercase">+ New Demands</p><p className="text-base font-bold text-red-600 mt-1" style={{ fontFamily: 'Manrope' }}>{fmt(bridge.new_demands)}</p></div>
            <div className="p-3 bg-emerald-50 rounded-xl"><p className="text-[9px] font-bold text-emerald-400 uppercase">− Collections</p><p className="text-base font-bold text-emerald-600 mt-1" style={{ fontFamily: 'Manrope' }}>{fmt(bridge.collections)}</p></div>
            <div className="p-3 bg-blue-50 rounded-xl border-2 border-blue-200"><p className="text-[9px] font-bold text-blue-400 uppercase">Closing</p><p className="text-base font-bold text-blue-700 mt-1" style={{ fontFamily: 'Manrope' }}>{fmt(bridge.closing_outstanding)}</p></div>
          </div>
        </CardContent>
      </Card>

      {/* ── 4. Collection Efficiency by Project ── */}
      {efficiency.length > 0 && (
        <Card className="bg-white rounded-2xl border-slate-200/60 shadow-sm" data-testid="efficiency">
          <CardHeader className="pb-2 border-b border-slate-100"><CardTitle className="text-sm font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>Collection Efficiency by Project</CardTitle></CardHeader>
          <CardContent className="pt-3">
            <div className="space-y-2">
              {efficiency.slice(0, 12).map((e, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="text-[10px] font-medium text-slate-600 w-32 sm:w-40 truncate flex-shrink-0">{e.project}</span>
                  <div className="flex-1 h-5 bg-slate-100 rounded-sm overflow-hidden relative">
                    <div className="h-full rounded-sm" style={{ width: `${Math.min(100, e.efficiency)}%`, background: e.efficiency >= 80 ? '#10B981' : e.efficiency >= 50 ? '#F59E0B' : '#EF4444' }} />
                  </div>
                  <span className={`text-[10px] font-bold w-12 text-right ${e.efficiency >= 80 ? 'text-emerald-600' : e.efficiency >= 50 ? 'text-amber-600' : 'text-red-600'}`}>{e.efficiency}%</span>
                  <span className="text-[9px] text-slate-400 w-20 text-right">{fmt(e.collected)}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── 2. Customer-wise Outstanding by Project ── */}
      <Card className="bg-white rounded-2xl border-slate-200/60 shadow-sm" data-testid="project-customers">
        <CardHeader className="pb-2 border-b border-slate-100">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>Customer-wise Outstanding by Project</CardTitle>
            <span className="text-[10px] text-slate-400">{projects.length} projects</span>
          </div>
        </CardHeader>
        <CardContent className="pt-3">
          <div className="space-y-1.5">
            {projects.map((p, i) => (
              <div key={i}>
                <button onClick={() => toggleProject(p.project)} className="w-full flex items-center justify-between p-3 bg-slate-50 hover:bg-slate-100 rounded-xl transition-colors" data-testid={`project-toggle-${i}`}>
                  <div className="flex items-center gap-2"><Building2 className="h-4 w-4 text-blue-500" /><span className="text-xs font-semibold text-slate-800">{p.project}</span><span className="text-[9px] text-slate-400">{p.customer_count} customers</span></div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-bold text-red-600">{fmt(p.total_outstanding)}</span>
                    <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${expandedProjects[p.project] ? 'rotate-180' : ''}`} />
                  </div>
                </button>
                {expandedProjects[p.project] && (
                  <div className="mt-1 mx-2 overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead><tr className="border-b border-slate-200">
                        <th className="text-left py-1.5 px-2 font-semibold text-slate-600">Customer</th>
                        <th className="text-left py-1.5 px-2 font-semibold text-slate-600">Unit</th>
                        <th className="text-right py-1.5 px-2 font-semibold text-blue-600">Demanded</th>
                        <th className="text-right py-1.5 px-2 font-semibold text-emerald-600">Received</th>
                        <th className="text-right py-1.5 px-2 font-semibold text-red-600">Outstanding</th>
                        <th className="text-right py-1.5 px-2 font-semibold text-amber-600">Interest</th>
                        <th className="text-right py-1.5 px-2 font-semibold text-slate-600">Ageing</th>
                      </tr></thead>
                      <tbody>
                        {p.customers.map((c, j) => (
                          <tr key={j} className={`border-b border-slate-100 ${j % 2 === 0 ? '' : 'bg-slate-50/50'}`}>
                            <td className="py-1.5 px-2 text-slate-800 max-w-[150px] truncate">{c.customer}</td>
                            <td className="py-1.5 px-2 text-slate-600">{c.unit}</td>
                            <td className="py-1.5 px-2 text-right text-blue-600">{fmt(c.demanded)}</td>
                            <td className="py-1.5 px-2 text-right text-emerald-600">{fmt(c.received)}</td>
                            <td className="py-1.5 px-2 text-right font-medium text-red-600">{fmt(c.outstanding)}</td>
                            <td className="py-1.5 px-2 text-right text-amber-600">{fmt(c.interest)}</td>
                            <td className="py-1.5 px-2 text-right"><span className={`inline-block px-1.5 py-0.5 rounded-full text-[9px] font-bold ${c.ageing > 90 ? 'bg-red-100 text-red-700' : c.ageing > 30 ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>{c.ageing}d</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* ── 6. Customer Payment Behavior ── */}
      {behavior.length > 0 && (
        <Card className="bg-white rounded-2xl border-slate-200/60 shadow-sm" data-testid="behavior">
          <CardHeader className="pb-2 border-b border-slate-100"><CardTitle className="text-sm font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>Customer Payment Behavior — Top 30</CardTitle></CardHeader>
          <CardContent className="pt-3">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead><tr className="border-b border-slate-200">
                  <th className="text-left py-2 px-2 font-semibold text-slate-600">Customer</th>
                  <th className="text-left py-2 px-2 font-semibold text-slate-600">Project</th>
                  <th className="text-right py-2 px-2 font-semibold text-red-600">Outstanding</th>
                  <th className="text-right py-2 px-2 font-semibold text-slate-600">Collection %</th>
                  <th className="text-right py-2 px-2 font-semibold text-slate-600">Ageing</th>
                  <th className="text-center py-2 px-2 font-semibold text-slate-600">Grade</th>
                </tr></thead>
                <tbody>
                  {behavior.map((b, i) => (
                    <tr key={i} className={`border-b border-slate-100 ${i % 2 === 0 ? '' : 'bg-slate-50/50'}`}>
                      <td className="py-1.5 px-2 text-slate-800 max-w-[150px] truncate">{b.customer}</td>
                      <td className="py-1.5 px-2 text-slate-600 max-w-[120px] truncate">{b.project}</td>
                      <td className="py-1.5 px-2 text-right font-medium text-red-600">{fmt(b.outstanding)}</td>
                      <td className="py-1.5 px-2 text-right text-slate-600">{b.collection_pct}%</td>
                      <td className="py-1.5 px-2 text-right text-slate-600">{b.ageing}d</td>
                      <td className="py-1.5 px-2 text-center"><span className={`inline-block px-2 py-0.5 rounded-full text-[9px] font-bold ${GRADE_COLORS[b.grade]}`}>{b.grade}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── 7. Upcoming Pipeline ── */}
      <Card className="bg-white rounded-2xl border-slate-200/60 shadow-sm" data-testid="pipeline">
        <CardHeader className="pb-2 border-b border-slate-100">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>Upcoming Collection Pipeline (Non-Demanded)</CardTitle>
            <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-0.5">
              {[["next_30", "30d"], ["next_60", "60d"], ["next_90", "90d"]].map(([k, label]) => (
                <button key={k} onClick={() => setPipelineTab(k)} className={`px-2.5 py-1 text-[10px] font-semibold rounded-md transition-all ${pipelineTab === k ? 'bg-[#1D4ED8] text-white' : 'text-slate-500'}`}>{label}</button>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-3">
          <div className="flex items-center gap-4 mb-3">
            <div className="p-3 bg-emerald-50 rounded-xl"><p className="text-[9px] font-bold text-emerald-600 uppercase">Total</p><p className="text-lg font-bold text-emerald-700" style={{ fontFamily: 'Manrope' }}>{fmt(pipeline[pipelineTab]?.total)}</p></div>
            <div className="p-3 bg-slate-50 rounded-xl"><p className="text-[9px] font-bold text-slate-400 uppercase">Milestones</p><p className="text-lg font-bold text-slate-800" style={{ fontFamily: 'Manrope' }}>{pipeline[pipelineTab]?.count || 0}</p></div>
          </div>
          {(pipeline[pipelineTab]?.items || []).length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead><tr className="border-b border-slate-200">
                  <th className="text-left py-1.5 px-2 font-semibold text-slate-600">Project</th>
                  <th className="text-left py-1.5 px-2 font-semibold text-slate-600">Customer</th>
                  <th className="text-left py-1.5 px-2 font-semibold text-slate-600">Unit</th>
                  <th className="text-left py-1.5 px-2 font-semibold text-slate-600">Event</th>
                  <th className="text-center py-1.5 px-2 font-semibold text-slate-600">Due Date</th>
                  <th className="text-right py-1.5 px-2 font-semibold text-emerald-600">Amount</th>
                </tr></thead>
                <tbody>
                  {(pipeline[pipelineTab]?.items || []).map((item, i) => (
                    <tr key={i} className={`border-b border-slate-100 ${i % 2 === 0 ? '' : 'bg-slate-50/50'}`}>
                      <td className="py-1.5 px-2 text-slate-800 truncate max-w-[120px]">{item.project}</td>
                      <td className="py-1.5 px-2 text-slate-600 truncate max-w-[120px]">{item.customer}</td>
                      <td className="py-1.5 px-2 text-slate-600">{item.unit}</td>
                      <td className="py-1.5 px-2 text-slate-500 truncate max-w-[150px]">{item.event}</td>
                      <td className="py-1.5 px-2 text-center text-slate-600">{item.due_date}</td>
                      <td className="py-1.5 px-2 text-right font-medium text-emerald-600">{fmt(item.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <p className="text-xs text-slate-400 text-center py-4">No milestones in this period</p>}
        </CardContent>
      </Card>
    </div>
  );
}
